// Screener stages. The backend split screening into two stages; older competitions carry
// one flat object, which means stage 1.
import type { SweScreener, SweScreenerStage, Timeframe } from "@/lib/api/types";

export type StageNo = 1 | 2;

/**
 * One stage of the screener, or null when the miner never reached it. A legacy flat
 * screener counts as stage 1 - but only if it actually carries a value, so an empty
 * object does not turn into a stage that was never run.
 */
export function screenerStage(
  screener: SweScreener | null | undefined,
  stage: StageNo,
): SweScreenerStage | null {
  if (!screener) return null;
  const nested = stage === 1 ? screener.stage1 : screener.stage2;
  if (nested) return nested;
  if (stage === 2 || screener.stage1 || screener.stage2) return null;

  const flat =
    screener.score ??
    screener.screener_passed ??
    screener.token_savings_ratio ??
    screener.verified_token_savings_ratio ??
    screener.explore_token_savings_ratio ??
    screener.edit_token_savings_ratio;
  if (flat === null || flat === undefined) return null;

  return {
    score: screener.score ?? null,
    baseline_weighted_tokens: screener.baseline_weighted_tokens ?? null,
    miner_weighted_tokens: screener.miner_weighted_tokens ?? null,
    token_savings_ratio: screener.token_savings_ratio ?? null,
    verified_token_savings_ratio: screener.verified_token_savings_ratio ?? null,
    explore_token_savings_ratio: screener.explore_token_savings_ratio ?? null,
    edit_token_savings_ratio: screener.edit_token_savings_ratio ?? null,
    screener_passed: screener.screener_passed ?? null,
  };
}

/** What a stage can say about itself. */
export type StageVerdict = "passed" | "failed" | "running" | "scheduled" | "none";

/**
 * When a stage can first have run. Stage 1 starts with the upload window; stage 2 only
 * once evaluation opens - the orchestrator returns before seeding any of it while the
 * upload window is still open:
 *
 *   # Stage 2 begins only once the upload window has closed (eval window open).
 *   if not in_eval_window:
 *       return created
 *
 * (SOMA/mcp_platform/app/services/swebench_orchestrator.py, read 2026-09-23.) Without
 * this the page shows stage 2 as pending for the whole upload week, when in fact nothing
 * is measuring it and nothing will for days.
 *
 * An unreadable timeframe counts as started: better to fall back to what the data says
 * than to invent a wait the competition may not have.
 */
export function stageStarted(timeframe: Timeframe | undefined, stage: StageNo, now: number): boolean {
  const at = stage === 1 ? timeframe?.upload_start : timeframe?.evaluation_start;
  const t = at ? Date.parse(at) : Number.NaN;
  return !Number.isFinite(t) || now >= t;
}

/**
 * A stage is a verdict only once it has been scored. The backend sets stage 2's
 * screener_passed the moment a miner clears stage 1 - its own comment says the flag
 * there means "qualified for full evaluation", not "passed stage 2" - while score,
 * baseline and miner tokens are all still null. In a finished competition that same
 * stage carries a real score beside the flag (checked against CoT-Compression-8 and -10,
 * 2026-09-23). So a bare flag is a promise, not a result.
 *
 * `started` is whether this stage's window has opened; `live` whether the competition is
 * still producing numbers at all. The three unscored cases are different things and the
 * screen should not say the same thing about them: not due yet, being worked on, over
 * without ever running.
 */
export function stageVerdict(
  stage: SweScreenerStage | null,
  { started, live }: { started: boolean; live: boolean },
): StageVerdict {
  const scored = typeof stage?.score === "number" && Number.isFinite(stage.score);
  if (stage && scored) {
    if (stage.screener_passed === true) return "passed";
    if (stage.screener_passed === false) return "failed";
  }
  if (!started) return live ? "scheduled" : "none";
  return live ? "running" : "none";
}

/**
 * Statuses where the backend has work dispatched right now. Taken from how it assigns
 * them (SOMA/mcp_platform/app/api/routes/frontend.py): `evaluating` and `screening` mean
 * runs are out, `qualifying` means stage 1 is passed and the cohort's stage-2 ranking is
 * still resolving.
 *
 * `qualified` is not one of them - it is the resolved outcome of that ranking, and a
 * spinner on it says the backend is busy with a miner it has already finished deciding.
 * `in queue` and `pending` are not either: the backend's own comment for `in queue` is
 * "runs exist (created, pending) but none are currently dispatched - nothing actively
 * running right now", and the word already says the miner is waiting.
 */
const WORKING = new Set(["evaluating", "screening", "qualifying"]);

export function isWorking(status: string | null | undefined): boolean {
  return typeof status === "string" && WORKING.has(status);
}
